import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertTriangle, Circle, Sparkles, Search, MapPin, Send } from "lucide-react";
import { Button } from "../ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("auth_token")}` });

const CATS = [
  { id: "seo", title: "SEO – Auffindbarkeit bei Google", icon: Search },
  { id: "geo", title: "GEO – Lokale Sichtbarkeit & KI-Suche", icon: MapPin },
  { id: "google", title: "Google-Profil Checkliste", icon: CheckCircle2 },
];

const StatusIcon = ({ status }) =>
  status === "ok" ? <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
    : status === "warn" ? <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
      : <Circle className="w-5 h-5 text-slate-300 flex-shrink-0" />;

export function SeoSection({ pageId }) {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/pages/${pageId}/seo`, { headers: authHeaders() }).then(r => setReport(r.data)).catch(() => toast.error("SEO-Check konnte nicht geladen werden"));
  }, [pageId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (item, done) => {
    try {
      const res = await axios.put(`${API}/pages/${pageId}/seo-checklist`, { item, done }, { headers: authHeaders() });
      setReport(prev => ({ ...prev, ...res.data }));
    } catch { toast.error("Konnte nicht gespeichert werden"); }
  };

  const request = async (svc) => {
    setBusy(svc.id);
    try {
      await axios.post(`${API}/pages/${pageId}/seo-service-request`, { service_id: svc.id }, { headers: authHeaders() });
      toast.success("Anfrage gesendet – wir melden uns mit einem Angebot.");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Anfrage fehlgeschlagen"); }
    finally { setBusy(null); }
  };

  if (!report) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>;
  const open = new Set((report.requests || []).filter(r => r.status === "offen").map(r => r.service_id));
  const scoreColor = report.score >= 80 ? "text-emerald-600" : report.score >= 50 ? "text-amber-600" : "text-red-600";

  return (
    <div className="space-y-6" data-testid="seo-section">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 flex items-center gap-6 flex-wrap">
        <div className="text-center">
          <p className={`text-5xl font-bold ${scoreColor}`} data-testid="seo-score">{report.score}</p>
          <p className="text-xs text-slate-500 mt-1">von 100 Punkten</p>
        </div>
        <div className="flex-1 min-w-[220px]">
          <h2 className="font-bold text-slate-900 text-lg">SEO- & GEO-Check deiner Seite</h2>
          <p className="text-sm text-slate-500">Automatische Prüfung deiner Seitendaten. Google-Profil-Punkte hakst du selbst ab ({report.google_done}/{report.google_total} erledigt).</p>
          <p className="text-xs text-slate-400 mt-1" data-testid="seo-score-note">Der Wert misst technische Grundlagen und Vollständigkeit (70 %) sowie dein Google-Profil (30 %) – er ist keine Garantie für ein Google-Ranking.</p>
        </div>
      </div>

      {CATS.map(cat => (
        <div key={cat.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
            <cat.icon className="w-4 h-4 text-indigo-600" /><h3 className="font-semibold text-slate-900">{cat.title}</h3>
          </div>
          <ul className="divide-y divide-slate-50">
            {report.checks.filter(c => c.category === cat.id).map(c => (
              <li key={c.id} className="px-6 py-3.5 flex items-start gap-3" data-testid={`seo-check-${c.id}`}>
                {c.manual ? (
                  <input type="checkbox" checked={c.status === "ok"} onChange={e => toggle(c.id, e.target.checked)} className="mt-0.5 w-5 h-5 accent-indigo-600" data-testid={`seo-checklist-${c.id}`} />
                ) : <StatusIcon status={c.status} />}
                <div>
                  <p className="text-sm font-medium text-slate-900">{c.label}</p>
                  {!c.manual && <p className="text-xs text-slate-500 mt-0.5">{c.detail}</p>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}

      <div className="bg-gradient-to-br from-indigo-50 to-amber-50 rounded-2xl border border-indigo-100 p-6" data-testid="seo-services">
        <div className="flex items-center gap-2 mb-1"><Sparkles className="w-5 h-5 text-indigo-600" /><h3 className="font-bold text-slate-900">Optimierungen buchen</h3></div>
        <p className="text-sm text-slate-600 mb-5">Wir übernehmen die Umsetzung für dich. Preise erhältst du nach deiner Anfrage per E-Mail – unverbindlich.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {report.services.map(svc => (
            <div key={svc.id} className="bg-white rounded-xl border border-slate-100 p-4 flex flex-col">
              <h4 className="font-semibold text-slate-900 text-sm">{svc.title}</h4>
              <p className="text-xs text-slate-500 mt-1 flex-1">{svc.desc}</p>
              <Button size="sm" disabled={open.has(svc.id) || busy === svc.id} onClick={() => request(svc)} data-testid={`seo-service-${svc.id}`}
                className="mt-3 rounded-full bg-indigo-600 hover:bg-indigo-700 w-fit">
                {busy === svc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : open.has(svc.id) ? "Angefragt" : <><Send className="w-3.5 h-3.5 mr-1.5" />Angebot anfragen</>}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
